import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PageHeader from "../PageHeader";
import UserAvatar from "../people/UserAvatar";
import usePageParam from "../../hooks/usePageParam";
import { discussionApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { displayName, relativeTime } from "../tasks/userDisplay";
import { panelSummary } from "./discussionText";

const PAGE_SIZE = 12;

function DiscussionRow({ discussion: d }) {
  const badges = [];
  if (d.waiting_on_you) badges.push(["Waiting on your answer", "warning"]);
  if (d.answers_opened_for_you) badges.push(["Answers open", "warning"]);
  if (d.mentions_you) badges.push(["Mentions you", "warning"]);
  if (d.unread_count > 0) badges.push([`${d.unread_count} new`, "default"]);
  if (d.is_panel) badges.push([`Panel · ${panelSummary(d)}`, "secondary"]);
  if (d.decision) badges.push(["Decided", "success"]);

  return (
    <li className="rounded-lg border bg-card p-4 text-card-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/discussions/${d.id}`}
          className="font-display text-lg font-semibold underline-offset-4 hover:underline"
        >
          {d.title}
        </Link>
        {badges.map(([label, variant]) => (
          <span key={label}>
            {" "}
            <Badge variant={variant} className="font-normal">
              {label}
            </Badge>
          </span>
        ))}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span className="flex -space-x-1.5" aria-hidden="true">
          {d.participants.map((p) => (
            <UserAvatar
              key={p.id}
              user={p}
              size="xs"
              decorative
              className="ring-2 ring-card"
            />
          ))}
        </span>
        <span>
          Started by {displayName(d.user)} · {d.post_count}{" "}
          {d.post_count === 1 ? "post" : "posts"} · last activity{" "}
          <time dateTime={d.last_post_at} title={d.last_post_at}>
            {relativeTime(d.last_post_at)}
          </time>
        </span>
      </div>
    </li>
  );
}

DiscussionRow.propTypes = {
  discussion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    user: PropTypes.object,
    last_post_at: PropTypes.string,
    post_count: PropTypes.number,
    unread_count: PropTypes.number,
    waiting_on_you: PropTypes.bool,
    answers_opened_for_you: PropTypes.bool,
    mentions_you: PropTypes.bool,
    is_panel: PropTypes.bool,
    answers_open: PropTypes.bool,
    panelists: PropTypes.array,
    participants: PropTypes.array,
    decision: PropTypes.string,
  }).isRequired,
};

/**
 * The household's discussions (projects/discussions D13), newest activity
 * first. "For you" narrows to panels waiting on your answer, panels whose
 * answers just opened to you, and unread mentions — what an agent checks
 * at the start of a session, too. Both the filter and the page live in
 * the URL (`?show=for_me&page=2`), so refresh and Back keep them.
 */
function DiscussionList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const show = searchParams.get("show") === "for_me" ? "for_me" : "all";
  const [page, setPage] = usePageParam();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [quietTick, setQuietTick] = useState(0);
  const { forYouCount = null } = useOutletContext() || {};

  // Only the newest request lands (paging and switching race).
  const request = useRef(0);
  const load = useCallback(async ({ quiet = false } = {}) => {
    const id = ++request.current;
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    try {
      const params = { page };
      if (show === "for_me") params.for_me = true;
      const { data } = await discussionApi.list(params);
      if (id !== request.current) return;
      setRows(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      if (id !== request.current) return;
      // A background refresh that fails keeps what's on screen.
      if (quiet) return;
      if (err?.response?.status === 404 && page > 1) {
        setPage(1, { replace: true }); // a page that no longer exists
        return;
      }
      // The last answer was for another page or filter: don't show it
      // under this one.
      setRows([]);
      setCount(0);
      setError(apiErrorMessage(err, "Couldn't load discussions."));
    } finally {
      if (id === request.current && !quiet) setLoading(false);
    }
  }, [page, setPage, show]);

  useEffect(() => {
    load();
  }, [load, reloadTick]);

  // The nav's for-you count moved (a new mention, answers opened): what's
  // listed here has too. Refresh quietly, so the badge and the list never
  // say different things (review of #386).
  const latestLoad = useRef(load);
  latestLoad.current = load;
  const seenCount = useRef(forYouCount);
  useEffect(() => {
    const before = seenCount.current;
    seenCount.current = forYouCount;
    if (before === null || forYouCount === null || before === forYouCount)
      return;
    setQuietTick((t) => t + 1);
  }, [forYouCount]);
  useEffect(() => {
    if (quietTick) latestLoad.current({ quiet: true });
  }, [quietTick]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: 880 }}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Discussions"
          subtitle="Threads with your household — people and agents. Ask a panel to hear everyone out before anyone hears the first answer."
        />
        {/* mb-6, like the header beside it: when the button wraps under
            the title it keeps its distance from the filter below. Side by
            side, the taller header sets the row's height anyway. */}
        <Button asChild className="mb-6 shrink-0">
          <Link to="/discussions/new">Start a discussion</Link>
        </Button>
      </div>

      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={show}
        onValueChange={(v) => {
          if (!v) return;
          // One URL write: the filter, and back to page 1 (usePageParam).
          const next = new URLSearchParams(searchParams);
          if (v === "for_me") next.set("show", "for_me");
          else next.delete("show");
          next.delete("page");
          setSearchParams(next);
        }}
        aria-label="Show"
        className="mb-4"
      >
        <ToggleGroupItem value="all">All</ToggleGroupItem>
        <ToggleGroupItem value="for_me">For you</ToggleGroupItem>
      </ToggleGroup>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setReloadTick((t) => t + 1)}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading discussions…</span>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-lg border bg-card p-4 text-muted-foreground">
          {show === "for_me"
            ? "Nothing waiting on you: no panels to answer, no answers newly opened to you, no unread mentions."
            : "No discussions yet. Start one — or ask a panel: each panelist answers before seeing anyone else's answer."}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {rows.map((d) => (
            <DiscussionRow key={d.id} discussion={d} />
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination page={page} total={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

export default DiscussionList;
